/**
 * @fileoverview Job Team Panel
 *
 * Purpose: Displays job team members and allows owners to manage access.
 * Shows member list with roles, invite dialog, and remove/role change actions.
 *
 * Dependencies:
 * - Convex: jobs.queries.getJobMembers, jobs.members.*
 * - shadcn components
 *
 * Used by:
 * - Job detail layout (as a collapsible panel or embedded section)
 */

import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { JobRole } from "@cvx/jobs/schema";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/ui/composed/overlays/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { UserAvatar, getDisplayName } from "@/ui/composed";
import { getPresetPractitionerNames } from "@/lib/practitioner-presets";
import {
  UserPlus,
  Users,
  Building2,
  Loader2,
  ChevronsUpDown,
  X,
} from "lucide-react";
import { toast } from "sonner";

const UNASSIGNED = "__unassigned__";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface JobMemberItem {
  _id: Id<"jobMemberships">;
  role: JobRole;
  user: {
    _id: Id<"users">;
    email: string;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  };
}

interface JobTeamPanelProps {
  /** The job ID to show team for */
  jobId: Id<"jobs">;
  /** The organisation this job belongs to */
  orgId: Id<"organizations">;
  /** The current user's role on this job */
  userRole: JobRole;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  member: "Member",
  viewer: "Viewer",
};

const ROLE_VARIANTS: Record<string, "default" | "secondary" | "outline"> = {
  owner: "default",
  member: "secondary",
  viewer: "outline",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Panel showing job team members with management actions for owners.
 */
export function JobTeamPanel({ jobId, orgId, userRole }: JobTeamPanelProps) {
  const { data: members, isLoading } = useQuery(
    convexQuery(api.jobs.queries.getJobMembers, { jobId }),
  );

  const isOwner = userRole === "owner";

  return (
    <div className="flex flex-col gap-4 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Team</h3>
        {isOwner && (
          <div className="flex items-center gap-1.5">
            <AddAllStaffDialog jobId={jobId} />
            <AddTeamDialog jobId={jobId} orgId={orgId} />
            <InviteDialog jobId={jobId} orgId={orgId} />
          </div>
        )}
      </div>

      <AppointeesSection
        jobId={jobId}
        orgId={orgId}
        canEdit={userRole !== "viewer"}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : !members || members.length === 0 ? (
        <p className="text-sm text-muted-foreground">No team members.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {members.map((member: JobMemberItem) => (
            <div
              key={member._id}
              className="flex items-center justify-between gap-2 rounded-md p-2 hover:bg-muted/50"
            >
              <div className="flex min-w-0 items-center gap-2">
                <UserAvatar
                  avatarUrl={member.user.avatarUrl}
                  firstName={member.user.firstName}
                  lastName={member.user.lastName}
                  email={member.user.email}
                  className="h-7 w-7"
                />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium">
                    {getDisplayName(
                      member.user.firstName,
                      member.user.lastName,
                      member.user.email,
                    )}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {member.user.email}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {isOwner ? (
                  <>
                    <RoleSelector
                      jobId={jobId}
                      userId={member.user._id}
                      currentRole={member.role}
                    />
                    {member.role !== "owner" ? (
                      <RemoveMemberButton
                        jobId={jobId}
                        userId={member.user._id}
                        name={getDisplayName(
                          member.user.firstName,
                          member.user.lastName,
                          member.user.email,
                        )}
                      />
                    ) : (
                      <div className="h-7 w-7" />
                    )}
                  </>
                ) : (
                  <Badge
                    variant={ROLE_VARIANTS[member.role] ?? "outline"}
                    className="text-xs"
                  >
                    {ROLE_LABELS[member.role] ?? member.role}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <WorkflowRolesSection jobId={jobId} orgId={orgId} isOwner={isOwner} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Appointees (MOR-951)
// ---------------------------------------------------------------------------

/**
 * The formally appointed practitioner names on the job (`jobs.appointees`).
 * Previously only settable at job creation via the create-job dialog's
 * Appointee checkbox — a wrong pick (or a partner leaving the firm) had no
 * correction path. Owners and members can add, remove and change appointees
 * here; suggestions come from the firm's registered practitioner presets and
 * the job team, with free-text entry for anyone else.
 */
function AppointeesSection({
  jobId,
  orgId,
  canEdit,
}: {
  jobId: Id<"jobs">;
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId }),
  );
  const { data: allPresets } = useQuery(
    convexQuery(api.settings.precedents.presets.listByOrg, { orgId }),
  );
  const { data: members } = useQuery(
    convexQuery(api.jobs.queries.getJobMembers, { jobId }),
  );
  const { mutateAsync: updateJob, isPending } = useMutation({
    mutationFn: useConvexMutation(api.jobs.mutations.updateJob),
  });

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const appointees = useMemo(() => job?.appointees ?? [], [job?.appointees]);

  // Registered practitioners (Settings → Fields → Practitioner Details) are
  // the canonical appointee source; job members cover in-house picks. Anyone
  // else can be typed in.
  const suggestions = useMemo(() => {
    const taken = new Set(appointees.map((name) => name.toLowerCase()));
    const names = new Set<string>(getPresetPractitionerNames(allPresets));
    for (const member of (members ?? []) as JobMemberItem[]) {
      const name = getDisplayName(
        member.user.firstName,
        member.user.lastName,
        member.user.email,
      );
      if (name) names.add(name);
    }
    return Array.from(names)
      .filter((name) => !taken.has(name.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
  }, [allPresets, members, appointees]);

  const commit = async (next: string[], successMessage: string) => {
    try {
      await updateJob({ jobId, appointees: next });
      toast.success(successMessage);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not update the appointees",
      );
    }
  };

  const addAppointee = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setOpen(false);
    setQuery("");
    if (appointees.some((a) => a.toLowerCase() === trimmed.toLowerCase())) {
      toast.info(`${trimmed} is already an appointee`);
      return;
    }
    void commit([...appointees, trimmed], `${trimmed} added as an appointee`);
  };

  const removeAppointee = (name: string) => {
    void commit(
      appointees.filter((a) => a !== name),
      `${name} removed as an appointee`,
    );
  };

  const trimmedQuery = query.trim();
  const showFreeTextAdd =
    trimmedQuery.length > 0 &&
    !suggestions.some(
      (name) => name.toLowerCase() === trimmedQuery.toLowerCase(),
    );

  return (
    <div className="flex flex-col gap-2 border-b pb-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h4 className="text-sm font-medium">Appointees</h4>
          <p className="text-xs text-muted-foreground">
            The formally appointed practitioner(s) on this job. Appointee names
            flow through to precedents, reports and the conflict brief.
          </p>
        </div>
        {canEdit && (
          <Popover
            open={open}
            onOpenChange={(next) => {
              setOpen(next);
              if (!next) setQuery("");
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 shrink-0 gap-1 px-2 text-xs"
                disabled={isPending || !job}
              >
                {isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <UserPlus className="h-3.5 w-3.5" />
                )}
                Add
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="end">
              <Command>
                <CommandInput
                  placeholder="Search or type a name…"
                  value={query}
                  onValueChange={setQuery}
                  className="h-9 text-xs"
                />
                <CommandList>
                  <CommandEmpty>Type a full name to add it.</CommandEmpty>
                  <CommandGroup>
                    {suggestions.map((name) => (
                      <CommandItem
                        key={name}
                        value={name}
                        onSelect={() => addAppointee(name)}
                        className="text-xs"
                      >
                        {name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {showFreeTextAdd && (
                    <CommandGroup forceMount>
                      <CommandItem
                        value={`add-${trimmedQuery}`}
                        forceMount
                        onSelect={() => addAppointee(trimmedQuery)}
                        className="text-xs"
                      >
                        <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                        Add “{trimmedQuery}”
                      </CommandItem>
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>
      {appointees.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No appointees designated.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {appointees.map((name) => (
            <Badge
              key={name}
              variant="secondary"
              className="gap-1 text-xs font-normal"
            >
              {name}
              {canEdit && (
                <button
                  type="button"
                  onClick={() => removeAppointee(name)}
                  disabled={isPending}
                  className="-mr-1 rounded-full p-0.5 hover:bg-muted-foreground/20"
                  title={`Remove ${name} as an appointee`}
                  aria-label={`Remove ${name} as an appointee`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workflow Roles (MOR-748)
// ---------------------------------------------------------------------------

/**
 * Who fills each firm-defined workflow role on this job. Owners can change
 * the assignment; doing so re-points the job's open role-stamped workflow
 * tasks to the new person (so a forgotten or mistaken pick at job creation
 * is fixed here in one step). Non-owners get a read-only view.
 */
function WorkflowRolesSection({
  jobId,
  orgId,
  isOwner,
}: {
  jobId: Id<"jobs">;
  orgId: Id<"organizations">;
  isOwner: boolean;
}) {
  const { data: assignments } = useQuery(
    convexQuery(api.jobs.roleAssignments.listByJob, { jobId }),
  );
  const { data: orgMembers } = useQuery(
    convexQuery(api.organizations.queries.getOrganizationMembers, { orgId }),
  );

  const { mutateAsync: setAssignment } = useMutation({
    mutationFn: useConvexMutation(api.jobs.roleAssignments.setAssignment),
  });

  if (!assignments || assignments.length === 0) return null;

  const handleChange = async (
    roleId: Id<"workflowRoles">,
    roleName: string,
    value: string,
  ) => {
    const userId = value === UNASSIGNED ? null : (value as Id<"users">);
    try {
      const result = await setAssignment({ jobId, roleId, userId });
      const holder = userId
        ? (() => {
            const m = (orgMembers ?? []).find((om) => om.user._id === userId);
            return m
              ? getDisplayName(m.user.firstName, m.user.lastName, m.user.email)
              : "the selected member";
          })()
        : "nobody";
      toast.success(
        `${roleName} assigned to ${holder}` +
          (result.reassigned > 0
            ? ` — ${result.reassigned} task${result.reassigned !== 1 ? "s" : ""} reassigned`
            : ""),
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update role";
      toast.error(message);
    }
  };

  return (
    <div className="flex flex-col gap-2 border-t pt-3">
      <div className="flex flex-col gap-0.5">
        <h4 className="text-sm font-medium">Workflow Roles</h4>
        <p className="text-xs text-muted-foreground">
          Workflow tasks assigned to a role go to the person filling it.
          Changing a role moves their open role-based tasks across.
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        {assignments.map((assignment) => (
          <div
            key={assignment.roleId}
            className="flex items-center justify-between gap-2 rounded-md p-1.5 hover:bg-muted/50"
          >
            <span className="text-sm">{assignment.name}</span>
            {isOwner ? (
              <Select
                value={assignment.user?._id ?? UNASSIGNED}
                onValueChange={(v) =>
                  handleChange(assignment.roleId, assignment.name, v)
                }
              >
                <SelectTrigger className="h-7 w-[180px] text-xs">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {(orgMembers ?? []).map((m) => (
                    <SelectItem key={m.user._id} value={m.user._id}>
                      {getDisplayName(
                        m.user.firstName,
                        m.user.lastName,
                        m.user.email,
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : assignment.user ? (
              <div className="flex items-center gap-1.5">
                <UserAvatar
                  avatarUrl={assignment.user.avatarUrl}
                  firstName={assignment.user.firstName}
                  lastName={assignment.user.lastName}
                  email={assignment.user.email}
                  className="h-5 w-5"
                />
                <span className="text-xs">
                  {getDisplayName(
                    assignment.user.firstName,
                    assignment.user.lastName,
                    assignment.user.email,
                  )}
                </span>
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">Unassigned</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Role Selector
// ---------------------------------------------------------------------------

function RoleSelector({
  jobId,
  userId,
  currentRole,
}: {
  jobId: Id<"jobs">;
  userId: Id<"users">;
  currentRole: JobRole;
}) {
  const { mutateAsync: updateRole } = useMutation({
    mutationFn: useConvexMutation(api.jobs.members.updateJobMemberRole),
  });

  const handleRoleChange = async (newRole: string) => {
    try {
      await updateRole({
        jobId,
        userId,
        role: newRole as JobRole,
      });
      toast.success(`Role updated to ${ROLE_LABELS[newRole] ?? newRole}`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update role";
      toast.error(message);
    }
  };

  return (
    <Select value={currentRole} onValueChange={handleRoleChange}>
      <SelectTrigger className="h-7 w-[100px] text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="owner">Owner</SelectItem>
        <SelectItem value="member">Member</SelectItem>
        <SelectItem value="viewer">Viewer</SelectItem>
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Remove Member Button
// ---------------------------------------------------------------------------

function RemoveMemberButton({
  jobId,
  userId,
  name,
}: {
  jobId: Id<"jobs">;
  userId: Id<"users">;
  name: string;
}) {
  const { mutateAsync: removeJobMember, isPending } = useMutation({
    mutationFn: useConvexMutation(api.jobs.members.removeJobMember),
  });

  const handleRemove = async () => {
    try {
      await removeJobMember({ jobId, userId });
      toast.success(`Removed ${name} from the job`);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to remove member";
      toast.error(message);
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
      onClick={handleRemove}
      disabled={isPending}
    >
      <X className="h-3.5 w-3.5" />
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Add All Staff Dialog (MOR-722)
// ---------------------------------------------------------------------------

/**
 * Adds every member of the organisation to the job in one click, behind a
 * confirmation dialog. Existing members keep their role; only missing
 * people are added, so it's safe to run repeatedly.
 */
function AddAllStaffDialog({ jobId }: { jobId: Id<"jobs"> }) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<JobRole>("member");

  const { mutateAsync: addAllStaff, isPending } = useMutation({
    mutationFn: useConvexMutation(api.jobs.members.addAllStaffToJob),
  });

  const handleConfirm = async () => {
    try {
      const result = await addAllStaff({ jobId, role });
      if (result.added === 0) {
        toast.info("Everyone in the organisation is already on this job");
      } else {
        toast.success(
          `Added ${result.added} member${result.added !== 1 ? "s" : ""} to the job` +
            (result.skipped > 0 ? ` (${result.skipped} already on it)` : ""),
        );
      }
      setOpen(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to add all staff";
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1 text-xs">
          <Building2 className="h-3.5 w-3.5" />
          Add All Staff
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add all staff to this job</DialogTitle>
          <DialogDescription>
            Everyone in your organisation will be given access to this job.
            People already on the job keep their current role.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Select value={role} onValueChange={(v) => setRole(v as JobRole)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">
                Member — can view and contribute
              </SelectItem>
              <SelectItem value="viewer">Viewer — read-only access</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="button" onClick={handleConfirm} disabled={isPending}>
            {isPending ? "Adding..." : "Add All Staff"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Add Team Dialog (MOR-722)
// ---------------------------------------------------------------------------

/**
 * Adds every member of a selected team to the job. Members already on the
 * job (e.g. via another team) are skipped so nobody is double-added.
 */
function AddTeamDialog({
  jobId,
  orgId,
}: {
  jobId: Id<"jobs">;
  orgId: Id<"organizations">;
}) {
  const [open, setOpen] = useState(false);
  const [teamId, setTeamId] = useState<Id<"teams"> | null>(null);
  const [role, setRole] = useState<JobRole>("member");

  const { data: teams } = useQuery(
    convexQuery(api.organizations.teams.listTeams, { orgId }),
  );

  const { mutateAsync: addTeam, isPending } = useMutation({
    mutationFn: useConvexMutation(api.jobs.members.addTeamToJob),
  });

  const handleConfirm = async () => {
    if (!teamId) {
      toast.error("Select a team");
      return;
    }
    try {
      const result = await addTeam({ jobId, teamId, role });
      if (result.added === 0) {
        toast.info(`Everyone in "${result.teamName}" is already on this job`);
      } else {
        toast.success(
          `Added ${result.added} member${result.added !== 1 ? "s" : ""} from "${result.teamName}"` +
            (result.skipped > 0 ? ` (${result.skipped} already on it)` : ""),
        );
      }
      setOpen(false);
      setTeamId(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to add team";
      toast.error(message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTeamId(null);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1 text-xs">
          <Users className="h-3.5 w-3.5" />
          Add Team
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a team to this job</DialogTitle>
          <DialogDescription>
            Every member of the team gets access. People already on the job keep
            their current role. Teams are managed in Organisation Settings.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <Select
            value={teamId ?? ""}
            onValueChange={(v) => setTeamId(v as Id<"teams">)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select team..." />
            </SelectTrigger>
            <SelectContent>
              {(teams ?? []).map((team) => (
                <SelectItem key={team._id} value={team._id}>
                  {team.name} ({team.members.length} member
                  {team.members.length !== 1 ? "s" : ""})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {teams && teams.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No teams yet. Create teams in Organisation Settings.
            </p>
          )}
          <Select value={role} onValueChange={(v) => setRole(v as JobRole)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">
                Member — can view and contribute
              </SelectItem>
              <SelectItem value="viewer">Viewer — read-only access</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isPending || !teamId}
          >
            {isPending ? "Adding..." : "Add Team"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Invite Dialog
// ---------------------------------------------------------------------------

function InviteDialog({
  jobId,
  orgId,
}: {
  jobId: Id<"jobs">;
  orgId: Id<"organizations">;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<Id<"users"> | null>(
    null,
  );
  const [role, setRole] = useState<JobRole>("member");

  const { data: orgMembers } = useQuery(
    convexQuery(api.organizations.queries.getOrganizationMembers, { orgId }),
  );

  const { data: jobMembers } = useQuery(
    convexQuery(api.jobs.queries.getJobMembers, { jobId }),
  );

  const availableMembers = useMemo(() => {
    if (!orgMembers) return [];
    const jobMemberIds = new Set(
      (jobMembers ?? []).map((m: JobMemberItem) => m.user._id),
    );
    return orgMembers.filter((m) => !jobMemberIds.has(m.user._id));
  }, [orgMembers, jobMembers]);

  const filteredMembers = useMemo(() => {
    if (!search.trim()) return availableMembers;
    const q = search.toLowerCase();
    return availableMembers.filter((m) => {
      const name = getDisplayName(
        m.user.firstName,
        m.user.lastName,
        m.user.email,
      ).toLowerCase();
      return name.includes(q) || m.user.email.toLowerCase().includes(q);
    });
  }, [availableMembers, search]);

  const selectedMember = availableMembers.find(
    (m) => m.user._id === selectedUserId,
  );

  const { mutateAsync: addJobMember, isPending } = useMutation({
    mutationFn: useConvexMutation(api.jobs.members.addJobMember),
  });

  const resetState = () => {
    setSelectedUserId(null);
    setRole("member");
    setPickerOpen(false);
    setSearch("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUserId) {
      toast.error("Select a team member");
      return;
    }

    try {
      await addJobMember({ jobId, userId: selectedUserId, role });
      const name = selectedMember
        ? getDisplayName(
            selectedMember.user.firstName,
            selectedMember.user.lastName,
            selectedMember.user.email,
          )
        : "Member";
      toast.success(`Added ${name} to the job`);
      setDialogOpen(false);
      resetState();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to add member";
      toast.error(message);
    }
  };

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetState();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1 text-xs">
          <UserPlus className="h-3.5 w-3.5" />
          Add Member
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Member to Job</DialogTitle>
            <DialogDescription>
              Select an organisation member to add to this job.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            {/* Member picker -- no portals, just inline DOM */}
            <div className="relative">
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={pickerOpen}
                className="w-full justify-between font-normal"
                type="button"
                onClick={() => {
                  setPickerOpen(!pickerOpen);
                  setSearch("");
                }}
              >
                {selectedMember ? (
                  <span className="flex items-center gap-2 truncate">
                    <UserAvatar
                      avatarUrl={selectedMember.user.avatarUrl}
                      firstName={selectedMember.user.firstName}
                      lastName={selectedMember.user.lastName}
                      email={selectedMember.user.email}
                      className="h-5 w-5"
                    />
                    {getDisplayName(
                      selectedMember.user.firstName,
                      selectedMember.user.lastName,
                      selectedMember.user.email,
                    )}
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    Select member...
                  </span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>

              {pickerOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                  <div className="flex items-center border-b px-3">
                    <input
                      type="text"
                      className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                      placeholder="Search by name or email..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="max-h-[200px] overflow-y-auto p-1">
                    {filteredMembers.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">
                        No members found.
                      </p>
                    ) : (
                      filteredMembers.map((member) => {
                        const name = getDisplayName(
                          member.user.firstName,
                          member.user.lastName,
                          member.user.email,
                        );
                        return (
                          <button
                            key={member.user._id}
                            type="button"
                            className="flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                            onClick={() => {
                              setSelectedUserId(member.user._id);
                              setPickerOpen(false);
                              setSearch("");
                            }}
                          >
                            <UserAvatar
                              avatarUrl={member.user.avatarUrl}
                              firstName={member.user.firstName}
                              lastName={member.user.lastName}
                              email={member.user.email}
                              className="h-7 w-7 shrink-0"
                            />
                            <div className="flex min-w-0 flex-col text-left">
                              <span className="truncate text-sm">{name}</span>
                              <span className="truncate text-xs text-muted-foreground">
                                {member.user.email}
                              </span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            <Select value={role} onValueChange={(v) => setRole(v as JobRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">
                  Member — can view and contribute
                </SelectItem>
                <SelectItem value="viewer">
                  Viewer — read-only access
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isPending || !selectedUserId}>
              {isPending ? "Adding..." : "Add to Job"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
